import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Image,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { useFocusEffect } from "expo-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import i18n from "../../locales/i18n";
import customAxios from "../../utils/customAxios";
import WebSidebar from "./WebSidebar";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import StatsImage from "../../assets/images/stats.png";
import ProfitIcon from "../../assets/web/statistic/Profit.png";
import CashierIcon from "../../assets/web/statistic/Cashier.png";
import DollarBagIcon from "../../assets/web/statistic/Dollar Bag.png";
import ProfitSecondIcon from "../../assets/web/statistic/Profit-second-card.png";

interface Facture {
  id_facture: number;
  total: number;
  profit: number;
  capital: number;
  listofproducts: {
    id_product: number;
    quantity: number;
    product_name: string;
    price_sell: string;
  }[];
  Userid: number;
  created_at: string;
}

interface StatsData {
  totalselles: number;
  total: number;
  totalprofit: number;
  totalcapital: number;
  factures: Facture[];
}

// Calendar Range Picker
const CalendarRangePicker: React.FC<{
  startDate: string;
  endDate: string;
  onSelectStart: (d: string) => void;
  onSelectEnd: (d: string) => void;
  onSearch: () => void;
}> = ({ startDate, endDate, onSelectStart, onSelectEnd, onSearch }) => {
  const [selecting, setSelecting] = useState<"start" | "end">("start");
  const [showCalendar, setShowCalendar] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const today = new Date();
  const [viewDate, setViewDate] = useState(() => new Date());
  const yearScrollRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!showCalendar) return;
    const handler = (e: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target as Node)
      ) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCalendar]);

  // Auto-scroll to selected year
  useEffect(() => {
    if (showYearPicker) {
      setTimeout(() => {
        if (yearScrollRef.current) {
          const el = yearScrollRef.current.querySelector(
            '[data-selected="true"]',
          );
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 80);
    }
  }, [showYearPicker]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const shiftedFirst = (firstDay + 1) % 7;

  const dayNames = [
    i18n.t("saturday")?.slice(0, 3),
    i18n.t("sunday")?.slice(0, 3),
    i18n.t("monday")?.slice(0, 3),
    i18n.t("tuesday")?.slice(0, 3),
    i18n.t("wednesday")?.slice(0, 3),
    i18n.t("thursday")?.slice(0, 3),
    i18n.t("friday")?.slice(0, 3),
  ];

  const monthNames = [
    "Jan",
    "Fév",
    "Mar",
    "Avr",
    "Mai",
    "Jun",
    "Jul",
    "Aoû",
    "Sep",
    "Oct",
    "Nov",
    "Déc",
  ];
  const monthNamesFull = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  const days: (number | null)[] = [];
  for (let i = 0; i < shiftedFirst; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const toKey = (d: number) =>
    `${year}-${(month + 1).toString().padStart(2, "0")}-${d.toString().padStart(2, "0")}`;

  const isStart = (d: number) => startDate === toKey(d);
  const isEnd = (d: number) => endDate === toKey(d);
  const isInRange = (d: number) => {
    if (!startDate || !endDate) return false;
    const key = toKey(d);
    return key > startDate && key < endDate;
  };
  const isTodayDay = (d: number) =>
    today.getFullYear() === year &&
    today.getMonth() === month &&
    today.getDate() === d;

  const handleDayClick = (d: number) => {
    const key = toKey(d);
    if (selecting === "start") {
      onSelectStart(key);
      if (endDate && key >= endDate) onSelectEnd("");
      setSelecting("end");
    } else {
      if (key <= startDate) {
        onSelectStart(key);
        onSelectEnd("");
        setSelecting("end");
      } else {
        onSelectEnd(key);
        setSelecting("start");
        setShowCalendar(false);
      }
    }
  };

  const fmtDisplay = (d: string) => {
    if (!d) return "--/--/----";
    const p = d.split("-");
    return `${p[2]}/${p[1]}/${p[0]}`;
  };

  const handleInputClick = (which: "start" | "end") => {
    setSelecting(which);
    setShowCalendar(true);
    setShowYearPicker(false);
  };

  const currentYear = today.getFullYear();
  const years = Array.from(
    { length: currentYear - 2015 + 2 },
    (_, i) => 2015 + i,
  );

  return (
    <div ref={calendarRef} style={{ position: "relative", width: "100%" }}>
      {/* Compact date input bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "linear-gradient(135deg, #F8FBFF 0%, #F0F7FC 100%)",
          borderRadius: 14,
          padding: "10px 14px",
          border: showCalendar ? "1.5px solid #1379A2" : "1.5px solid #E2EAF0",
          boxShadow: showCalendar
            ? "0 0 0 3px rgba(19,121,162,0.08)"
            : "0 1px 4px rgba(0,0,0,0.04)",
          transition: "all 0.25s ease",
          cursor: "pointer",
        }}
      >
        {/* Calendar icon */}
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: "linear-gradient(135deg, #1379A2 0%, #0D5F82 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(19,121,162,0.25)",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 16, color: "#fff" }}>📅</span>
        </div>

        {/* Start date */}
        <button
          onClick={() => handleInputClick("start")}
          style={{
            flex: 1,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "2px 0",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: "700",
              color: "#94A3B8",
              letterSpacing: 1,
              textTransform: "uppercase",
              marginBottom: 1,
            }}
          >
            {i18n.t("startDate")}
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: "700",
              fontFamily: "'SF Mono', 'Cascadia Code', monospace",
              color: startDate ? "#1379A2" : "#CBD5E1",
              transition: "color 0.2s",
            }}
          >
            {fmtDisplay(startDate)}
          </div>
        </button>

        {/* Arrow */}
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            backgroundColor: "#E8F4F8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <span style={{ color: "#1379A2", fontSize: 12, fontWeight: "bold" }}>
            →
          </span>
        </div>

        {/* End date */}
        <button
          onClick={() => handleInputClick("end")}
          style={{
            flex: 1,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "2px 0",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: "700",
              color: "#94A3B8",
              letterSpacing: 1,
              textTransform: "uppercase",
              marginBottom: 1,
            }}
          >
            {i18n.t("endDate")}
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: "700",
              fontFamily: "'SF Mono', 'Cascadia Code', monospace",
              color: endDate ? "#1379A2" : "#CBD5E1",
              transition: "color 0.2s",
            }}
          >
            {fmtDisplay(endDate)}
          </div>
        </button>

        {/* Search button */}
        <button
          onClick={() => {
            if (startDate && endDate) {
              onSearch();
              setShowCalendar(false);
            }
          }}
          disabled={!startDate || !endDate}
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            border: "none",
            cursor: startDate && endDate ? "pointer" : "default",
            background:
              startDate && endDate
                ? "linear-gradient(135deg, #1379A2 0%, #0D5F82 100%)"
                : "#E2EAF0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow:
              startDate && endDate ? "0 2px 8px rgba(19,121,162,0.25)" : "none",
            transition: "all 0.2s ease",
          }}
        >
          <span
            style={{
              color: startDate && endDate ? "#fff" : "#94A3B8",
              fontSize: 15,
            }}
          >
            🔍
          </span>
        </button>
      </div>

      {/* Absolute calendar dropdown */}
      {showCalendar && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: "#fff",
            borderRadius: 18,
            boxShadow:
              "0 12px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
            border: "1px solid rgba(19,121,162,0.1)",
            padding: 20,
            animation: "calendarSlideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          {/* Inline keyframes */}
          <style>{`
            @keyframes calendarSlideIn {
              from { opacity: 0; transform: translateY(-8px) scale(0.97); }
              to { opacity: 1; transform: translateY(0) scale(1); }
            }
            .cal-year-btn:hover { background-color: #EBF5FB !important; transform: scale(1.06) !important; }
            .cal-month-btn:hover { background-color: #EBF5FB !important; transform: scale(1.06) !important; }
            .cal-day-btn:hover { background-color: #EBF5FB !important; transform: scale(1.1) !important; }
            .cal-nav-btn:hover { background-color: #EBF5FB !important; }
            .cal-year-scroll::-webkit-scrollbar { width: 4px; }
            .cal-year-scroll::-webkit-scrollbar-track { background: transparent; }
            .cal-year-scroll::-webkit-scrollbar-thumb { background: #D1E3ED; border-radius: 4px; }
          `}</style>

          {/* Selecting indicator */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 6,
              marginBottom: 14,
            }}
          >
            <button
              onClick={() => {
                setSelecting("start");
              }}
              style={{
                padding: "5px 16px",
                borderRadius: 20,
                fontSize: 11,
                fontWeight: "700",
                border: "none",
                cursor: "pointer",
                backgroundColor: selecting === "start" ? "#1379A2" : "#F1F5F9",
                color: selecting === "start" ? "#fff" : "#64748B",
                transition: "all 0.2s ease",
                boxShadow:
                  selecting === "start"
                    ? "0 2px 8px rgba(19,121,162,0.3)"
                    : "none",
              }}
            >
              {i18n.t("startDate")}: {fmtDisplay(startDate)}
            </button>
            <button
              onClick={() => {
                setSelecting("end");
              }}
              style={{
                padding: "5px 16px",
                borderRadius: 20,
                fontSize: 11,
                fontWeight: "700",
                border: "none",
                cursor: "pointer",
                backgroundColor: selecting === "end" ? "#1379A2" : "#F1F5F9",
                color: selecting === "end" ? "#fff" : "#64748B",
                transition: "all 0.2s ease",
                boxShadow:
                  selecting === "end"
                    ? "0 2px 8px rgba(19,121,162,0.3)"
                    : "none",
              }}
            >
              {i18n.t("endDate")}: {fmtDisplay(endDate)}
            </button>
          </div>

          {/* Month/Year navigation */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 14,
              padding: "0 4px",
            }}
          >
            <button
              className="cal-nav-btn"
              onClick={() => setViewDate(new Date(year, month - 1, 1))}
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                backgroundColor: "#F1F5F9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                color: "#475569",
                transition: "all 0.15s",
              }}
            >
              ‹
            </button>
            <button
              onClick={() => setShowYearPicker(!showYearPicker)}
              style={{
                fontSize: 15,
                fontWeight: "800",
                color: "#1E293B",
                background: showYearPicker ? "#EBF5FB" : "transparent",
                border: "none",
                borderRadius: 10,
                padding: "6px 16px",
                cursor: "pointer",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {monthNamesFull[month]} {year}
              <span
                style={{
                  fontSize: 10,
                  color: "#94A3B8",
                  transition: "transform 0.2s",
                  display: "inline-block",
                  transform: showYearPicker ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                ▼
              </span>
            </button>
            <button
              className="cal-nav-btn"
              onClick={() => setViewDate(new Date(year, month + 1, 1))}
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                backgroundColor: "#F1F5F9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                color: "#475569",
                transition: "all 0.15s",
              }}
            >
              ›
            </button>
          </div>

          {/* Year & Month picker panel */}
          <div
            style={{
              maxHeight: showYearPicker ? 240 : 0,
              opacity: showYearPicker ? 1 : 0,
              overflow: "hidden",
              transition:
                "max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease",
            }}
          >
            <div
              style={{
                backgroundColor: "#F8FAFC",
                borderRadius: 14,
                padding: 12,
                marginBottom: 10,
                border: "1px solid #E8EDF2",
              }}
            >
              {/* Year grid */}
              <div
                className="cal-year-scroll"
                ref={yearScrollRef}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 4,
                  maxHeight: 110,
                  overflowY: "auto",
                  padding: "2px",
                  marginBottom: 10,
                }}
              >
                {years.map((y) => (
                  <button
                    key={y}
                    data-selected={y === year}
                    className="cal-year-btn"
                    onClick={() => setViewDate(new Date(y, month, 1))}
                    style={{
                      padding: "7px 4px",
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: y === year ? "800" : "500",
                      border:
                        y === year
                          ? "2px solid #1379A2"
                          : "1px solid transparent",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      backgroundColor: y === year ? "#1379A2" : "#fff",
                      color: y === year ? "#fff" : "#475569",
                      boxShadow:
                        y === year
                          ? "0 2px 8px rgba(19,121,162,0.25)"
                          : "0 1px 2px rgba(0,0,0,0.04)",
                    }}
                  >
                    {y}
                  </button>
                ))}
              </div>
              {/* Divider */}
              <div
                style={{
                  height: 1,
                  backgroundColor: "#E2E8F0",
                  margin: "0 -4px 10px",
                }}
              />
              {/* Month grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 4,
                }}
              >
                {monthNames.map((m, i) => (
                  <button
                    key={i}
                    className="cal-month-btn"
                    onClick={() => {
                      setViewDate(new Date(year, i, 1));
                      setShowYearPicker(false);
                    }}
                    style={{
                      padding: "8px 4px",
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: i === month ? "800" : "500",
                      border:
                        i === month
                          ? "2px solid #1379A2"
                          : "1px solid transparent",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      backgroundColor: i === month ? "#1379A2" : "#fff",
                      color: i === month ? "#fff" : "#475569",
                      boxShadow:
                        i === month
                          ? "0 2px 8px rgba(19,121,162,0.25)"
                          : "0 1px 2px rgba(0,0,0,0.04)",
                    }}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Days panel */}
          <div
            style={{
              maxHeight: showYearPicker ? 0 : 400,
              opacity: showYearPicker ? 0 : 1,
              overflow: "hidden",
              transition:
                "max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease",
            }}
          >
            {/* Day names header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 2,
                marginBottom: 6,
              }}
            >
              {dayNames.map((d, i) => (
                <div
                  key={i}
                  style={{
                    textAlign: "center",
                    fontSize: 11,
                    fontWeight: "700",
                    color: "#94A3B8",
                    padding: "4px 0",
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                  }}
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 2,
              }}
            >
              {days.map((day, i) => {
                if (!day)
                  return <div key={i} style={{ width: 36, height: 36 }} />;
                const start = isStart(day);
                const end = isEnd(day);
                const range = isInRange(day);
                const tod = isTodayDay(day);
                const selected = start || end;
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    {/* Range background */}
                    {(range || end) && startDate && (
                      <div
                        style={{
                          position: "absolute",
                          top: 4,
                          bottom: 4,
                          left: start ? "50%" : 0,
                          right: end ? "50%" : 0,
                          backgroundColor: "#E0F2FE",
                          zIndex: 0,
                          borderRadius: start
                            ? "8px 0 0 8px"
                            : end
                              ? "0 8px 8px 0"
                              : 0,
                        }}
                      />
                    )}
                    {(range || start) && endDate && startDate && (
                      <div
                        style={{
                          position: "absolute",
                          top: 4,
                          bottom: 4,
                          left: start ? "50%" : 0,
                          right: end ? "50%" : 0,
                          backgroundColor: "#E0F2FE",
                          zIndex: 0,
                        }}
                      />
                    )}
                    <button
                      className={
                        !selected && !range ? "cal-day-btn" : undefined
                      }
                      onClick={() => handleDayClick(day)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: selected ? 12 : 10,
                        border: "none",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: selected ? "800" : tod ? "700" : "500",
                        position: "relative",
                        zIndex: 1,
                        background: selected
                          ? "linear-gradient(135deg, #1379A2 0%, #0D5F82 100%)"
                          : range
                            ? "#E0F2FE"
                            : tod
                              ? "#F0F9FF"
                              : "transparent",
                        color: selected
                          ? "#fff"
                          : tod
                            ? "#1379A2"
                            : range
                              ? "#0C4A6E"
                              : "#334155",
                        transition: "all 0.15s ease",
                        boxShadow: selected
                          ? "0 2px 8px rgba(19,121,162,0.3)"
                          : "none",
                      }}
                    >
                      {day}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Screen
const WebDashboardScreen: React.FC = () => {
  const [stats, setStats] = useState<StatsData>({
    totalselles: 0,
    total: 0,
    totalprofit: 0,
    totalcapital: 0,
    factures: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState("month");
  const [chartData, setChartData] = useState<
    { name: string; revenue: number }[]
  >([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const { locale } = useSelector((state: RootState) => state.app);

  const isRTL = locale === "ar" || i18n.locale === "ar";

  const dateFilters = [
    { label: i18n.t("tout"), value: "all" },
    {
      label: i18n.t("year"),
      value: "year",
    },
    {
      label: i18n.t("mounth"),
      value: "month",
    },
    {
      label: i18n.t("today"),
      value: "today",
    },
  ];

  const getDateRange = (
    filter: string,
  ): { start: string; end: string } | null => {
    const now = new Date();
    const fmt = (d: Date) =>
      `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}/${d.getFullYear()}`;
    switch (filter) {
      case "today":
        return { start: fmt(now), end: fmt(now) };
      case "month": {
        const s = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start: fmt(s), end: fmt(now) };
      }
      case "year": {
        const s = new Date(now.getFullYear(), 0, 1);
        return { start: fmt(s), end: fmt(now) };
      }
      case "all":
      default:
        return null;
    }
  };

  const formatIntervalDate = (iso: string): string => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${m}/${d}/${y}`;
  };

  const buildChartFromFactures = (factures: any[]) => {
    const grouped: Record<string, number> = {};
    factures.forEach((f: any) => {
      const d = new Date(f.created_at);
      const key = `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}`;
      if (!grouped[key]) grouped[key] = 0;
      grouped[key] += (f.capital || 0) + (f.profit || 0);
    });
    const sorted = Object.entries(grouped)
      .sort(([a], [b]) => {
        const [da, ma] = a.split("/").map(Number);
        const [db, mb] = b.split("/").map(Number);
        return ma !== mb ? ma - mb : da - db;
      })
      .slice(-14)
      .map(([name, revenue]) => ({ name, revenue }));
    setChartData(sorted);
  };

  const fetchDashboardData = async (filter: string = "month") => {
    setIsLoading(true);
    try {
      const range = getDateRange(filter);

      let statsRes;
      if (range) {
        statsRes = await customAxios
          .get("/statistic_intervalle", {
            params: { start: range.start, end: range.end },
            withCredentials: true,
          })
          .catch(() => ({ data: {} }));
      } else {
        // For "all" filter, use a wide date range
        const now = new Date();
        const fiveYearsAgo = new Date(
          now.getFullYear() - 5,
          now.getMonth(),
          now.getDate(),
        );
        const fmt = (d: Date) =>
          `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}/${d.getFullYear()}`;
        statsRes = await customAxios
          .get("/statistic_intervalle", {
            params: { start: fmt(fiveYearsAgo), end: fmt(now) },
            withCredentials: true,
          })
          .catch(() => ({ data: {} }));
      }

      const factures = statsRes.data?.factures || [];

      setStats({
        totalselles: statsRes.data?.totalselles || 0,
        total: statsRes.data?.total || 0,
        totalprofit: statsRes.data?.totalprofit || 0,
        totalcapital: statsRes.data?.totalcapital || 0,
        factures,
      });

      buildChartFromFactures(factures);
    } catch {
      // silently ignore
    } finally {
      setIsLoading(false);
    }
  };

  const fetchByRange = async (start: string, end: string) => {
    if (!start || !end) return;
    setIsLoading(true);
    try {
      const rangeRes = await customAxios
        .get("/statistic_intervalle", {
          params: {
            start: formatIntervalDate(start),
            end: formatIntervalDate(end),
          },
          withCredentials: true,
        })
        .catch(() => ({ data: {} }));

      const factures = rangeRes.data?.factures || [];

      setStats({
        totalselles: rangeRes.data?.totalselles || 0,
        total: rangeRes.data?.total || 0,
        totalprofit: rangeRes.data?.totalprofit || 0,
        totalcapital: rangeRes.data?.totalcapital || 0,
        factures,
      });

      buildChartFromFactures(factures);
    } catch {
      // silently ignore
    } finally {
      setIsLoading(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      fetchDashboardData(selectedFilter);
    }, []),
  );

  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter);
    setStartDate("");
    setEndDate("");
    fetchDashboardData(filter);
  };

  const handleDateRangeSearch = () => {
    if (startDate && endDate) {
      setSelectedFilter("custom");
      fetchByRange(startDate, endDate);
    }
  };

  const formatFactureDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()} ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  };

  return (
    <div
      key={`dashboard-screen-${locale}`}
      className="flex flex-1 bg-[#F5F7FA] min-h-screen flex-row"
    >
      <WebSidebar />
      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={styles.headerSection}>
            <div
              className={`flex flex-row items-center gap-4 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
            >
              <View style={styles.headerIconContainer}>
                <Image
                  source={StatsImage}
                  style={styles.headerIcon}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.headerTitle}>
                {i18n.t("statistics") ||
                  "\u0627\u0644\u0627\u062d\u0635\u0627\u0626\u064a\u0627\u062a"}
              </Text>
            </div>
            <View style={styles.headerBar} />
          </View>

          {/* Date Filter Tabs */}
          <View style={styles.filterSection}>
            <View style={styles.filterTabs}>
              {dateFilters.map((filter) => (
                <TouchableOpacity
                  key={filter.value}
                  style={[
                    styles.filterTab,
                    selectedFilter === filter.value && styles.filterTabActive,
                  ]}
                  onPress={() => handleFilterChange(filter.value)}
                >
                  <Text
                    style={[
                      styles.filterTabText,
                      selectedFilter === filter.value &&
                        styles.filterTabTextActive,
                    ]}
                  >
                    {filter.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#1379A2" />
            </View>
          ) : (
            <View style={styles.dashboardContent}>
              {/* Stats Cards Row */}
              <View style={styles.statsCardsGrid}>
                <View style={styles.statCard}>
                  <View style={styles.statCardHeader}>
                    <Image
                      source={ProfitIcon}
                      style={styles.statIconImg}
                      resizeMode="contain"
                    />
                    <Text style={styles.statLabel}>{i18n.t("profit")}</Text>
                  </View>
                  <Text style={[styles.statValue, { color: "#2E7D32" }]}>
                    <Text style={styles.currency}>{i18n.t("currency")}</Text>{" "}
                    {stats.totalprofit.toLocaleString()}
                  </Text>
                </View>
                <View style={styles.statCard}>
                  <View style={styles.statCardHeader}>
                    <Image
                      source={ProfitSecondIcon}
                      style={styles.statIconImg}
                      resizeMode="contain"
                    />
                    <Text style={styles.statLabel}>{i18n.t("revenue")}</Text>
                  </View>
                  <Text style={[styles.statValue, { color: "#1565C0" }]}>
                    <Text style={styles.currency}>{i18n.t("currency")}</Text>{" "}
                    {stats.total.toLocaleString()}
                  </Text>
                </View>
                <View style={styles.statCard}>
                  <View style={styles.statCardHeader}>
                    <Image
                      source={DollarBagIcon}
                      style={styles.statIconImg}
                      resizeMode="contain"
                    />
                    <Text style={styles.statLabel}>{i18n.t("capital")}</Text>
                  </View>
                  <Text style={[styles.statValue, { color: "#00695C" }]}>
                    <Text style={styles.currency}>{i18n.t("currency")}</Text>{" "}
                    {stats.totalcapital.toLocaleString()}
                  </Text>
                </View>
                <View style={styles.statCard}>
                  <View style={styles.statCardHeader}>
                    <Image
                      source={CashierIcon}
                      style={styles.statIconImg}
                      resizeMode="contain"
                    />
                    <Text style={styles.statLabel}>{i18n.t("salesCount")}</Text>
                  </View>
                  <Text style={[styles.statValue, { color: "#6A1B9A" }]}>
                    {stats.totalselles.toLocaleString()}
                  </Text>
                </View>
              </View>

              {/* Chart + Calendar Row */}
              <View style={styles.chartCalendarRow}>
                {/* Bar Chart */}
                <View
                  style={[styles.lineChartCard, { flex: 1, minWidth: 300 }]}
                >
                  <Text style={styles.chartTitle}>{i18n.t("revenue")}</Text>
                  {chartData.length > 0 ? (
                    <>
                      <View style={styles.lineChartWrapper}>
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart
                            data={chartData}
                            margin={{
                              top: 10,
                              right: 20,
                              left: 10,
                              bottom: 10,
                            }}
                          >
                            <XAxis
                              dataKey="name"
                              tick={{ fill: "#666", fontSize: 11 }}
                              axisLine={{ stroke: "#E0E0E0" }}
                              tickLine={false}
                            />
                            <YAxis
                              tick={{ fill: "#999", fontSize: 11 }}
                              axisLine={{ stroke: "#E0E0E0" }}
                              tickLine={false}
                              tickFormatter={(v) => v.toLocaleString()}
                            />
                            <Tooltip
                              formatter={(value: any) => [
                                `${Number(value).toLocaleString()} ${i18n.t("currency")}`,
                                i18n.t("revenue"),
                              ]}
                              contentStyle={{
                                backgroundColor: "#333",
                                border: "none",
                                borderRadius: 8,
                              }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Bar
                              dataKey="revenue"
                              fill="#6D7BDB"
                              radius={[6, 6, 0, 0]}
                              barSize={22}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </View>
                    </>
                  ) : (
                    <View style={{ alignItems: "center", padding: 40 }}>
                      <Text style={{ color: "#94A3B8", fontSize: 14 }}>
                        {i18n.t("nodata")}
                      </Text>
                    </View>
                  )}
                </View>

                {/* Calendar */}
                <View
                  style={{
                    width: 340,
                    flexShrink: 0,
                    zIndex: 100,
                    overflow: "visible" as any,
                  }}
                >
                  <CalendarRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onSelectStart={setStartDate}
                    onSelectEnd={setEndDate}
                    onSearch={handleDateRangeSearch}
                  />
                </View>
              </View>

              {/* Factures Table */}
              {stats.factures.length > 0 && (
                <View style={styles.facturesCard}>
                  <View style={styles.facturesHeader}>
                    <Text style={styles.facturesTitle}>
                      {i18n.t("bills")} ({stats.factures.length})
                    </Text>
                  </View>
                  {/* Table header */}
                  <div
                    style={{
                      overflowX: "auto" as any,
                      WebkitOverflowScrolling: "touch" as any,
                    }}
                  >
                    <div style={{ minWidth: 700 }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "60px 1fr 120px 120px 120px 160px",
                          gap: 8,
                          padding: "10px 16px",
                          backgroundColor: "#F8FAFC",
                          borderRadius: 8,
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: "700",
                            color: "#64748B",
                            textTransform: "uppercase",
                          }}
                        >
                          #
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: "700",
                            color: "#64748B",
                            textTransform: "uppercase",
                          }}
                        >
                          {i18n.t("product")}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: "700",
                            color: "#64748B",
                            textTransform: "uppercase",
                            textAlign: "right",
                          }}
                        >
                          {i18n.t("revenue")}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: "700",
                            color: "#64748B",
                            textTransform: "uppercase",
                            textAlign: "right",
                          }}
                        >
                          {i18n.t("profit")}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: "700",
                            color: "#64748B",
                            textTransform: "uppercase",
                            textAlign: "right",
                          }}
                        >
                          {i18n.t("capital")}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: "700",
                            color: "#64748B",
                            textTransform: "uppercase",
                            textAlign: "right",
                          }}
                        >
                          {i18n.t("date")}
                        </span>
                      </div>
                      {/* Table rows */}
                      <div style={{ maxHeight: 400, overflowY: "auto" }}>
                        {stats.factures.map((facture, idx) => (
                          <div
                            key={facture.id_facture}
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "60px 1fr 120px 120px 120px 160px",
                              gap: 8,
                              padding: "12px 16px",
                              borderBottom:
                                idx < stats.factures.length - 1
                                  ? "1px solid #F1F5F9"
                                  : "none",
                              alignItems: "center",
                              transition: "background-color 0.15s",
                            }}
                            className="facture-row"
                          >
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: "600",
                                color: "#94A3B8",
                              }}
                            >
                              {facture.id_facture}
                            </span>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              {facture.listofproducts.map((p, pIdx) => (
                                <span
                                  key={pIdx}
                                  style={{
                                    fontSize: 13,
                                    color: "#334155",
                                    fontWeight: "500",
                                  }}
                                >
                                  {p.product_name}{" "}
                                  <span
                                    style={{ color: "#94A3B8", fontSize: 11 }}
                                  >
                                    ×{p.quantity}
                                  </span>
                                </span>
                              ))}
                            </div>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: "600",
                                color: "#1565C0",
                                textAlign: "right",
                              }}
                            >
                              {facture.total.toLocaleString()}{" "}
                              {i18n.t("currency")}
                            </span>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: "600",
                                color: "#2E7D32",
                                textAlign: "right",
                              }}
                            >
                              {facture.profit.toLocaleString()}{" "}
                              {i18n.t("currency")}
                            </span>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: "500",
                                color: "#64748B",
                                textAlign: "right",
                              }}
                            >
                              {facture.capital.toLocaleString()}{" "}
                              {i18n.t("currency")}
                            </span>
                            <span
                              style={{
                                fontSize: 12,
                                color: "#94A3B8",
                                textAlign: "right",
                                fontFamily: "'SF Mono', monospace",
                              }}
                            >
                              {formatFactureDate(facture.created_at)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Inline hover style */}
                  <style>{`
                    .facture-row:hover { background-color: #F8FBFF !important; }
                  `}</style>
                </View>
              )}
            </View>
          )}
        </ScrollView>
      </View>
    </div>
  );
};

const styles = StyleSheet.create({
  mainContent: { flex: 1 },
  scrollView: { flex: 1, padding: 24 },
  headerSection: { marginBottom: 20 },
  headerTitle: { fontSize: 32, fontWeight: "bold", color: "#333" },
  headerIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 4,
    borderColor: "#1379A2",
    backgroundColor: "#E8F4FC",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  headerIcon: { width: 55, height: 55 },
  headerBar: { height: 8, backgroundColor: "#1379A2", borderRadius: 4 },
  filterSection: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 24,
  },
  filterTabs: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 1,
  },
  filterTab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: "center",
    borderRadius: 6,
  },
  filterTabActive: { backgroundColor: "#1379A2" },
  filterTabText: { fontSize: 16, color: "#666", fontWeight: "500" },
  filterTabTextActive: { color: "#FFFFFF", fontWeight: "600" },
  loadingContainer: { padding: 60, alignItems: "center" },
  dashboardContent: { gap: 24 },
  statsCardsGrid: {
    flexDirection: "row",
    flexWrap: "wrap" as any,
    gap: 16,
  },
  statCard: {
    flex: 1,
    minWidth: 200,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 6,
    elevation: 2,
  },
  statCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    marginBottom: 12,
  },
  statIconImg: { width: 28, height: 28 },
  statLabel: { fontSize: 16, color: "#888", fontWeight: "500" },
  statValue: { fontSize: 26, fontWeight: "bold", textAlign: "right" },
  currency: { fontSize: 16, fontWeight: "500" },
  chartCalendarRow: {
    flexDirection: "row",
    gap: 20,
    flexWrap: "wrap" as any,
    alignItems: "flex-start",
    zIndex: 10,
    overflow: "visible" as any,
  },
  facturesCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 6,
    elevation: 2,
    zIndex: 1,
    position: "relative" as any,
  },
  facturesHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  facturesTitle: {
    fontSize: 18,
    fontWeight: "700" as any,
    color: "#1E293B",
  },
  lineChartCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 6,
    elevation: 2,
  },
  lineChartWrapper: { alignItems: "center", marginVertical: 10 },
  chartTitle: { fontSize: 16, color: "#888", marginBottom: 8 },
  lineLegend: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 32,
    marginTop: 12,
  },
  lineLegendItem: { flexDirection: "row", alignItems: "center", gap: 8 },
  lineLegendDot: { width: 12, height: 12, borderRadius: 6 },
  lineLegendText: { fontSize: 14, color: "#666" },
});

export default WebDashboardScreen;
